import { useEffect, useMemo, useRef, useState } from "react";
import type { SurfDeck } from "@vida/surf-core";
import { resolveSurfPreviewImageUrl } from "@vida/surf-core";
import { createSurfApiConfig, ensureSurfClientConfig, getPdfPresignedUrl } from "../services/surfClientConfig";
import { downscaleImageBlob, READER_IMAGE_MAX_WIDTH } from "../utils/downscaleImageBlob";
import { prepareRasterDisplayUrl } from "../utils/rasterDisplayPrep";
import { assetCacheKey, getCachedAssetUrl } from "../utils/surfAssetCache";
import {
  collectSurfDeckAssetItems,
  surfDeckAssetFingerprint,
} from "../utils/surfDeckAssets";

export type UseSurfAssetUrlsOptions = {
  /** Downscale rasters for small thumbnails (speaker reader only). */
  readerMode?: boolean;
};

/** Presigned URLs for slide backgrounds and image-layer s3 keys. */
export function useSurfAssetUrls(
  deck: SurfDeck | null,
  { readerMode = false }: UseSurfAssetUrlsOptions = {},
) {
  const [backgroundUrls, setBackgroundUrls] = useState<Record<string, string>>({});
  const [layerImageUrls, setLayerImageUrls] = useState<Record<string, string>>({});
  const [themePreviewUrl, setThemePreviewUrl] = useState<string | null>(null);

  const fingerprint = useMemo(() => (deck ? surfDeckAssetFingerprint(deck) : ""), [deck]);
  const deckRef = useRef(deck);
  deckRef.current = deck;
  const readerMaxW = readerMode ? READER_IMAGE_MAX_WIDTH : undefined;

  useEffect(() => {
    const activeDeck = deckRef.current;
    if (!activeDeck || !fingerprint) {
      setBackgroundUrls({});
      setLayerImageUrls({});
      setThemePreviewUrl(null);
      return;
    }

    const items = collectSurfDeckAssetItems(activeDeck);
    let cancelled = false;

    void (async () => {
      try {
        await ensureSurfClientConfig();
        const cfg = createSurfApiConfig();
        const presigned = getPdfPresignedUrl();
        if (!presigned) return;

        const bg: Record<string, string> = {};
        const layers: Record<string, string> = {};

        await Promise.all(
          items.map(async (item) => {
            const cacheKey = assetCacheKey(
              item.key,
              item.chromaKeyPrep ? item.chromaHex : undefined,
              readerMaxW,
            );
            const url = await getCachedAssetUrl(cacheKey, async () => {
              const presignedUrl = await resolveSurfPreviewImageUrl(
                presigned,
                () => cfg.getIdToken(),
                item.key,
              );
              let blob: Blob;
              if (item.chromaKeyPrep && item.chromaHex) {
                const prepared = await prepareRasterDisplayUrl(presignedUrl, item.chromaHex);
                const res = await fetch(prepared, { cache: "no-store" });
                blob = await res.blob();
                if (prepared.startsWith("blob:")) URL.revokeObjectURL(prepared);
              } else {
                const res = await fetch(presignedUrl, { cache: "no-store" });
                if (!res.ok) throw new Error(`Asset ${res.status}`);
                blob = await res.blob();
              }
              if (readerMaxW) {
                blob = await downscaleImageBlob(blob, readerMaxW);
              }
              return blob;
            });
            if (item.layerId) layers[item.layerId] = url;
            else bg[item.slideId] = url;
          }),
        );

        const previewKey = activeDeck.theme.previewS3Key?.trim();
        let preview: string | null = null;
        if (previewKey) {
          try {
            preview = await getCachedAssetUrl(
              assetCacheKey(previewKey, undefined, readerMaxW),
              async () => {
                const presignedUrl = await resolveSurfPreviewImageUrl(
                  presigned,
                  () => cfg.getIdToken(),
                  previewKey,
                );
                const res = await fetch(presignedUrl, { cache: "no-store" });
                if (!res.ok) throw new Error(`Preview ${res.status}`);
                let blob = await res.blob();
                if (readerMaxW) {
                  blob = await downscaleImageBlob(blob, readerMaxW);
                }
                return blob;
              },
            );
          } catch {
            preview = null;
          }
        }

        if (!cancelled) {
          setBackgroundUrls(bg);
          setLayerImageUrls(layers);
          setThemePreviewUrl(preview);
        }
      } catch {
        /* keep existing URLs on transient errors */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fingerprint, readerMaxW]);

  return { backgroundUrls, layerImageUrls, themePreviewUrl };
}
